import { CART_TARGET_ATTR, FlyOrigin, emitCartBump, subscribeFlyToCart } from "@/lib/fly-to-cart";
import { DECORATIVE_Z_INDEX } from "@/contexts/ModalContext";
import { NoImage } from "@/components/no-image/NoImage";
import { EASE, MORPH_RADIUS } from "@/lib/motion";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Surface } from "@/components/ui/Surface";

const MotionSurface = motion.create(Surface);

/** Biggest the ghost can get, however large the source photo is. */
const GHOST_MAX_SIZE = 88;
const GHOST_MIN_SIZE = 48;
/** Diameter the ghost shrinks to as it drops into the bag. */
const LANDING_SIZE = 22;
const FLIGHT_DURATION = 0.68;
/** Rises on the enter curve, drops on the exit curve. */
const FLIGHT_EASE = [EASE.enter, EASE.exit];
/**
 * How high above the straight line the arc peaks. Randomised per flight so a
 * burst of taps throws a scatter of arcs instead of the same one on repeat.
 */
const ARC_LIFT_MIN = 130;
const ARC_LIFT_MAX = 230;
/** Keeps the apex from sailing off the top of the screen. */
const ARC_TOP_MARGIN = 16;
/**
 * Start / apex / landing opacity. Solid enough to read as the item the customer
 * just tapped, translucent enough to read as its ghost — and it only really
 * fades on the last leg, as it drops into the bag.
 */
const GHOST_OPACITY = [0.88, 0.82, 0.35];
/** Frames to wait for the cart CTA, which only mounts on the first add. */
const TARGET_LOOKUP_FRAMES = 12;

type Flight = FlyOrigin & {
  id: number;
  size: number;
  /** Translation from the ghost's start to the bag, in px. */
  dx: number;
  dy: number;
  /** Apex offset of the arc, and how far along it sits. */
  lift: number;
  bow: number;
  /** ±1 — which way the ghost tumbles. */
  spin: number;
};

function findTargetCenter(): { x: number; y: number } | null {
  const element = document.querySelector(`[${CART_TARGET_ATTR}]`);
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/**
 * The bag button renders only once the cart has items, so on the very first add
 * it does not exist yet when the customer taps `+`. Poll a few frames before
 * giving up and aiming at the bottom of the screen.
 */
function resolveTargetCenter(
  onResolved: (center: { x: number; y: number }, hasTarget: boolean) => void,
  framesLeft = TARGET_LOOKUP_FRAMES,
): void {
  const center = findTargetCenter();
  if (center) return onResolved(center, true);
  if (framesLeft <= 0) {
    return onResolved({ x: window.innerWidth / 2, y: window.innerHeight - 48 }, false);
  }
  requestAnimationFrame(() => resolveTargetCenter(onResolved, framesLeft - 1));
}

/**
 * Draws the item's ghost arcing from its photo into the cart button, then tells
 * the button to bounce. Mounted once, near the router — a portal on `<body>`
 * keeps the ghost alive across the route change that "Adicionar" triggers.
 */
export function FlyToCartLayer() {
  const reduceMotion = useReducedMotion();
  const [flights, setFlights] = useState<Flight[]>([]);
  const nextId = useRef(0);

  const removeFlight = useCallback((id: number) => {
    setFlights((current) => current.filter((flight) => flight.id !== id));
  }, []);

  useEffect(() => {
    if (reduceMotion) return;

    return subscribeFlyToCart((origin) => {
      const size = Math.min(
        GHOST_MAX_SIZE,
        Math.max(GHOST_MIN_SIZE, Math.min(origin.width, origin.height)),
      );

      resolveTargetCenter((center) => {
        const dx = center.x - origin.centerX;
        const dy = center.y - origin.centerY;
        const bow = 0.34 + Math.random() * 0.24;

        const wanted = ARC_LIFT_MIN + Math.random() * (ARC_LIFT_MAX - ARC_LIFT_MIN);
        const headroom = origin.centerY + dy * 0.35 - (size / 2 + ARC_TOP_MARGIN);
        const lift = Math.max(0, Math.min(wanted, headroom));

        setFlights((current) => [
          ...current,
          {
            ...origin,
            id: nextId.current++,
            size,
            dx,
            dy,
            lift,
            bow,
            spin: Math.random() < 0.5 ? 1 : -1,
          },
        ]);
      });
    });
  }, [reduceMotion]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {flights.map((flight) => (
        <MotionSurface
          surface="card"
          key={flight.id}
          aria-hidden
          data-testid="fly-to-cart-ghost"
          className="pointer-events-none fixed overflow-hidden shadow-float ring-1 ring-border"
          style={{
            zIndex: DECORATIVE_Z_INDEX,
            left: flight.centerX - flight.size / 2,
            top: flight.centerY - flight.size / 2,
            width: flight.size,
            height: flight.size,
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: GHOST_OPACITY[0], borderRadius: MORPH_RADIUS.tile }}
          animate={{
            x: [0, flight.dx * flight.bow, flight.dx],
            y: [0, flight.dy * 0.35 - flight.lift, flight.dy],
            scale: [1, 0.72, LANDING_SIZE / flight.size],
            opacity: GHOST_OPACITY,
            rotate: [0, -8 * flight.spin, 6 * flight.spin],
            borderRadius: [MORPH_RADIUS.tile, MORPH_RADIUS.circle],
          }}
          transition={{
            duration: FLIGHT_DURATION,
            times: [0, 0.45, 1],
            ease: FLIGHT_EASE,
            borderRadius: { duration: FLIGHT_DURATION * 0.6, ease: EASE.enter },
          }}
          onAnimationComplete={() => {
            emitCartBump("add");
            removeFlight(flight.id);
          }}
        >
          {flight.imageSrc ? (
            <img src={flight.imageSrc} alt="" className="h-full w-full object-cover" />
          ) : (
            <NoImage className="h-full w-full" />
          )}
          <span className="absolute inset-0 bg-on-media/15" />
        </MotionSurface>
      ))}
    </AnimatePresence>,
    document.body,
  );
}
